import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaUserCircle } from "react-icons/fa";
import Loader from '../components/Loader';
import '../styles/Wallets.css';
import { getTemplate, getPreview } from '../lib/funding';
import { sendPaypalEmail } from '../lib/paypalEmail';
import { sendCashAppEmail } from '../lib/cashappEmail';
import { sendCoinbaseEmail } from '../lib/coinbaseEmail';
import { sendBybitEmail } from '../lib/bybitEmail';
import { sendBinanceEmail } from '../lib/binanceEmail';

const AdminFunding = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    
    const [activeTab, setActiveTab] = useState('edit');
    const [exchange, setExchange] = useState('Binance');
    
    const [formData, setFormData] = useState({});
    const [balance, setBalance] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const template = getTemplate(exchange);

    useEffect(() => {
        const fetchCurrentUserRole = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;

            if (!currentUser) {
                setCurrentUserRole("unauthenticated");
                setAuthLoading(false);
                setLoading(false);
                return;
            }

            try {
                const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (currentUserDoc.exists()) {
                    const userData = currentUserDoc.data();
                    const role = userData.role || "user";
                    setCurrentUserRole(role);

                    const userTransactions = userData.transactions || [];
                    const calculatedBalance = userTransactions.reduce((total, txn) => {
                        return txn.type === "credit" ? total + txn.amount : total - txn.amount;
                    }, 0);
                    setBalance(calculatedBalance);
                } else {
                    setCurrentUserRole("unknown");
                }
            } catch (error) {
                console.error("Error fetching current user role:", error);
                setCurrentUserRole("error");
            } finally {
                setAuthLoading(false);
                setLoading(false);
            }
        };

        fetchCurrentUserRole();
    }, []);

    useEffect(() => {
        setFormData({});
    }, [exchange]);

    const handleFieldChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (exchange === 'PayPal') {
                const transactionId = Math.random().toString(36).substring(2, 15).toUpperCase();
                const date = new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

                await sendPaypalEmail(formData.email, {
                    userName: formData.receiverName,
                    senderName: formData.senderName,
                    amount: formData.amount,
                    currency: formData.currency,
                    transactionId: transactionId,
                    date: date,
                    exchange: exchange,
                    fallbackEmail: formData.fallbackEmail
                });
            } else if (exchange === 'Cash App') {
                await sendCashAppEmail(formData.email, {
                    senderName: formData.senderName,
                    senderHandle: formData.senderHandle,
                    receiverName: formData.receiverName,
                    receiverHandle: formData.receiverHandle,
                    amount: formData.amount,
                    description: formData.description,
                    exchange: exchange,
                    fallbackEmail: formData.fallbackEmail
                });
            } else if (exchange === 'Coinbase') {
                await sendCoinbaseEmail(formData.email, {
                    amount: formData.amount,
                    currency: formData.currency,
                    exchange: exchange,
                    fallbackEmail: formData.fallbackEmail
                });
            } else if (exchange === 'Bybit') {
                await sendBybitEmail(formData.email, {
                    amount: formData.amount,
                    currency: formData.currency,
                    chainType: formData.chainType,
                    walletAddress: formData.walletAddress,
                    exchange: exchange,
                    fallbackEmail: formData.fallbackEmail
                });
            } else if (exchange === 'Binance') {
                await sendBinanceEmail(formData.email, {
                    amount: formData.amount,
                    currency: formData.currency,
                    exploreLink: formData.exploreLink,
                    exchange: exchange,
                    fallbackEmail: formData.fallbackEmail
                });
            }

            // Create debit transaction for the current user
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (currentUser) {
                const transaction = {
                    amount: 1,
                    description: `Funding fee for ${exchange}`,
                    date: new Date().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    type: 'debit',
                    beneficiary: exchange,
                    account: 'Savings',
                };
                await updateDoc(doc(db, 'users', currentUser.uid), {
                    transactions: arrayUnion(transaction),
                });
                setBalance(prev => prev - 1);
            }
            
            setSubmitting(false);
            setIsSuccess(true);
            setFormData({});
            
            setTimeout(() => setIsSuccess(false), 5000);
        } catch (error) {
            console.error("Error submitting funding:", error);
            setSubmitting(false);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading || authLoading) return <Loader />;

    if (currentUserRole !== "admin" && currentUserRole !== "moderator") {
        return (
            <div className="web3-user-not-found">
                <FaUserCircle size={48} />
                <h2>Access Denied</h2>
                <div onClick={handleGoBack} className="route-Links">
                    Go Back
                </div>
            </div>
        );
    }

    const exchanges = ['Binance', 'Bybit', 'Coinbase', 'Cash App', 'PayPal'];

    const tabStyle = (isActive) => ({
        padding: '10px 24px',
        cursor: 'pointer',
        background: isActive ? '#D52A2A' : 'transparent',
        color: isActive ? '#fff' : '#aaa',
        border: isActive ? 'none' : '1px solid #444',
        borderRadius: '8px',
        fontWeight: '600',
        fontSize: '14px',
        transition: 'all 0.3s ease',
    });

    const renderFormField = (field) => {
        const value = formData[field.key] || '';
        
        return (
            <div key={field.key} style={{ marginBottom: '15px' }}>
                <label>{field.label}</label>
                {field.type === 'number' ? (
                    <input 
                        className="input-field"
                        type="number"
                        placeholder={field.placeholder}
                        value={value}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        required={field.required}
                    />
                ) : field.type === 'email' ? (
                    <input 
                        className="input-field"
                        type="email"
                        placeholder={field.placeholder}
                        value={value}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        required={field.required}
                    />
                ) : (
                    <input 
                        className="input-field"
                        type="text"
                        placeholder={field.placeholder}
                        value={value}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        required={field.required}
                    />
                )}
            </div>
        );
    };

    const PreviewComponent = getPreview(exchange);

    return (
        <div className="wallets-container">
            <h2>Admin Funding</h2>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button onClick={() => setActiveTab('edit')} style={tabStyle(activeTab === 'edit')}>Edit</button>
                <button onClick={() => setActiveTab('preview')} style={tabStyle(activeTab === 'preview')}>Preview</button>
            </div>
            
            <div className="wallet-box">
                <div className="funding-row">
                    <div className="wallet-right">
                        {activeTab === 'edit' ? (
                            <>
                                <div className="wallet-header">
                                    <h3>{template?.exchange?.toUpperCase() || 'FUNDING DETAILS'}</h3>
                                </div>

                                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                    <label>Exchange / Platform</label>
                                    <select 
                                        className="input-field"
                                        value={exchange}
                                        onChange={(e) => setExchange(e.target.value)}
                                        style={{ marginBottom: '15px' }}
                                    >
                                        {exchanges.map(ex => (
                                            <option key={ex} value={ex}>{ex}</option>
                                        ))}
                                    </select>

                                    {template?.fields.map(renderFormField)}

                                    <div className="plan-actions">
                                        <button 
                                            type="submit" 
                                            className="action-btn trade"
                                            disabled={submitting || (currentUserRole === 'moderator' && balance < 1)}
                                            style={{ width: '100%' }}
                                        >
                                            {submitting ? "Processing..." : "Submit Funding"}
                                        </button>
                                    </div>
                                    {currentUserRole === 'moderator' && (
                                        <div style={{ textAlign: 'right', marginTop: '8px', color: balance < 1 ? '#ff4444' : '#28a745', fontWeight: 'bold' }}>
                                            Balance: ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    )}
                                </form>

                                {isSuccess && (
                                    <div className="wallet-info" style={{ 
                                        marginTop: '15px', 
                                        color: '#28a745', 
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        width: '100%'
                                    }}>
                                        Success! Your funding request has been processed.
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {PreviewComponent && <div className="preview-wrapper"><PreviewComponent data={formData} /></div>}

                                <div className="plan-actions" style={{ marginTop: '20px' }}>
                                    <button 
                                        type="button"
                                        onClick={handleSubmit}
                                        className="action-btn trade"
                                        disabled={submitting || (currentUserRole === 'moderator' && balance < 1)}
                                        style={{ width: '100%' }}
                                    >
                                        {submitting ? "Processing..." : "Submit Funding"}
                                    </button>
                                </div>
                                {currentUserRole === 'moderator' && (
                                    <div style={{ textAlign: 'right', marginTop: '8px', color: balance < 1 ? '#ff4444' : '#28a745' }}>
                                        Balance: ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                )}

                                {isSuccess && (
                                    <div className="wallet-info" style={{ 
                                        marginTop: '15px', 
                                        color: '#28a745', 
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        width: '100%'
                                    }}>
                                        Success! Your funding request has been processed.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminFunding;